// © Microsoft Corporation. All rights reserved.

import { ProfileCardTelemetryObjects, profileCardTelemetryContracts } from "./ProfileCard.telemetry";
import { addOrUpdateTelemetryObject, renderHybrid } from "../../Social.telemetry";
import { ellipsis, ellipsis2, exclamation, open_outside, pen } from "../../utils/icons";
import { hasThemeBase, isComponentDisabled } from "../../utils/themeHelpers";

import DisabledMessage from "../../comment/disabled-message/DisabledMessage";
import FollowButton from "../../follow-button/FollowButton";
import { NeutralButton } from "@microsoft/fast-components-react-msft";
import ProfileCardUserStats from "./profile-card-user-stats/ProfileCardUserStats";
import ProfileReportMenu from "../profile-report-menu/ProfileReportMenu";
import React from "react";
import { SocialProps } from "../../Social.props";
import avatarFallback from "../../utils/avatarFallback";
import { classNames } from "@microsoft/fast-web-utilities";
import { enums } from "../../utils/enums";
import { getContextData } from "@msnews/core";
import openEditProfilePage from "../../utils/openEditProfilePage";
import { placeholderImages } from "../../utils/images";

export interface ProfileCardProps extends SocialProps {
    emptyState?: string;
}

interface ProfileCardState {
    reportMenuVisible: boolean;
    selectedReportType: string;
    selectedReportItemType: string;
    isReported: boolean
}

export default class ProfileCard extends React.Component<ProfileCardProps, ProfileCardState> {
    containerRef: HTMLDivElement;
    telemetryObjects: ProfileCardTelemetryObjects;

    constructor(props) {
        super(props);
        this.state = {
            reportMenuVisible: false,
            selectedReportType: null,
            selectedReportItemType: null,
            isReported: false
        };
        this.containerRef = null;
        this.telemetryObjects = null;
    }

    getAvatarAltText() {
        const { me, t } = this.props;

        if (me.nameStatus == enums.userNameStatusType.notAllow) {
            return t.NameNotAllowTip.replace("{#}", me.name);
        } else if (me.avatarStatus == enums.userAvatarStatusType.notAllow) {
            return t.AvatarNotAllowTip;
        } else if (me.nameStatus == enums.userNameStatusType.inReview) {
            return t.NameInReviewTip;
        } else if (me.avatarStatus == enums.userAvatarStatusType.inReview) {
            return t.AvatarInReviewTip;
        } else if (me.isBanned) {
            return t.UserBannedTip;
        } else {
            return t.AvatarAltText;
        }
    }

    /* REPORTING */
    showReportMenu() {
        const { theme, popupConnector, id } = this.props;
        const isMobile = this.props.responsiveClass.includes(enums.responsiveLevels.L1);
        const isEmbeded = isComponentDisabled(theme, "profilereportmenu");

        if (isEmbeded || isMobile) {
            popupConnector.openPopup("profilereportmenu", { id: id });
        } else {
            this.setState({
                reportMenuVisible: true
            });
        }
    }

    /**
 * @param reportType: value from enums.userNameReportMap or enums.userPictureReportMap
 * @param reportItemType: value from enums.profileReportItemMap
 */
    reportHandler(reportType, reportItemType) {
        this.setState({
            isReported: true,
            reportMenuVisible: false,
            selectedReportType: reportType,
            selectedReportItemType: reportItemType
        });
    }

    openOnboardingPopup() {
        this.props.popupConnector.openPopup("onboardingPage");
    }

    render() {
        const userId = this.props.id;
        const { me, t, responsiveClass, users, managedClasses, market, emptyState, mainTelemetryObject, theme } = this.props;
        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);
        const isMobile = responsiveClass.includes(enums.responsiveLevels.L0);

        if (!me) { return null; }
        if (!users) { return null; }
        const user = users[userId] ? users[userId].data : null;

        if (!user || user.isBanned) { return null; }

        const { primaryAvatar, name, postsCount, followersCount, likesCount, traceId } = user;
        const isSelf = me && me.id == userId;

        let modifiedName: any;
        if (emptyState == enums.profilePageEmptyStates.NoUser) {
            modifiedName == t.ProfileNoUserUsername;
        } else if (emptyState == enums.profilePageEmptyStates.NotSocialUser) {
            modifiedName = t.ProfileNotSocialUsername;
        } else {
            modifiedName = name;
        }
        const avatarUrl = emptyState == enums.profilePageEmptyStates.NotSocialUser ? placeholderImages.notSocialAvatar : primaryAvatar || placeholderImages.avatar;
        const modifiedAvatarUrl = me.state != enums.userStateType.newUser && me.avatarStatus == enums.userAvatarStatusType.normal ? primaryAvatar || placeholderImages.avatar : placeholderImages.avatar;
        const avatarAltText = isSelf && this.getAvatarAltText();
        const meHasInReviewInfo = isSelf && (me.avatarStatus == enums.userAvatarStatusType.inReview || me.nameStatus == enums.userNameStatusType.inReview);
        const meHasNotAllowInfo = isSelf && (me.avatarStatus == enums.userAvatarStatusType.notAllow || me.nameStatus == enums.userNameStatusType.notAllow || (!meHasInReviewInfo && me.isBanned));

        /** Generate Telemetry Objects */
        const profileCardTelemetryObject = addOrUpdateTelemetryObject(mainTelemetryObject, profileCardTelemetryContracts.profileCard, { id: traceId }, this.telemetryObjects && this.telemetryObjects.profileCard);
        this.telemetryObjects = {
            profileCard: profileCardTelemetryObject,
            reportUserMenu: addOrUpdateTelemetryObject(profileCardTelemetryObject, profileCardTelemetryContracts.reportUserMenu, { id: traceId }, this.telemetryObjects && this.telemetryObjects.reportUserMenu),
            reportedMessage: addOrUpdateTelemetryObject(profileCardTelemetryObject, profileCardTelemetryContracts.reportedMessage, { id: traceId }, this.telemetryObjects && this.telemetryObjects.reportedMessage),
            moreButton: addOrUpdateTelemetryObject(profileCardTelemetryObject, profileCardTelemetryContracts.moreButton, { id: traceId }, this.telemetryObjects && this.telemetryObjects.moreButton)
        };

        const actionPanelHtml = this.calculateActionHTML({userId, isSelf, theme});

        return (
            <div
                role="main"
                className={classNames(
                    managedClasses.profileCardContainer,
                    [managedClasses.profileCardSuperAppContainer, isSuperApp],
                    responsiveClass,
                    getContextData(this.props.requestContext.data.locationHostName, "locationHostName") == "localhost.msn.com" ||
                        getContextData(this.props.requestContext.data.locationHostName, "locationHostName") == "assets.msn.com" ? "demo" : ""
                )}
                {...renderHybrid(this.telemetryObjects.profileCard, true, (el) => { this.containerRef = el; })}
            >
                <div className="cwt-user-info">
                    <div className="cwt-user-header">
                        <div className="cwt-avatar-container" title={isSelf ? avatarAltText : ""}>
                            <img alt={t.AriaLabelsProfilePicture} className="cwt-avatar" src={isSelf ? modifiedAvatarUrl : avatarUrl} onError={(e)=>{ avatarFallback(e); }}/>
                            { meHasInReviewInfo && <i className="cwt-icon cwt-status-mark cwt-inreview"> {ellipsis2} </i> }
                            { meHasNotAllowInfo && <i className="cwt-icon cwt-status-mark cwt-notallow"> {exclamation} </i> }
                        </div>
                        <div className={classNames(
                            "cwt-name-title",
                            ["cwt-name-title-self", isSelf]
                        )}>
                            <div className="cwt-name">{modifiedName}</div>
                            <ProfileCardUserStats {...{ ...this.props, isSelf, postsCount, followersCount, likesCount}} />
                            {(isMobile || isSuperApp) && actionPanelHtml}
                        </div>
                    </div>
                    {!(isMobile || isSuperApp) && actionPanelHtml}
                </div>
            </div>
        );
    }

    calculateActionHTML(actionHTMLParams) {
        const {isSelf, userId, theme} = actionHTMLParams;
        const { reportMenuVisible, isReported } = this.state;
        const { t, managedClasses, usersConnector, market, emptyState } = this.props;
        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);

        let profilePageEmptyStates: any;
        if (isSuperApp) {
            if (emptyState == enums.profilePageEmptyStates.NotSocialUser) {
                profilePageEmptyStates = (<a className="cwt-link">
                    <i className="cwt-icon cwt-link cwt-edit-profile-btn" onClick={this.openOnboardingPopup.bind(this)} role="button" aria-label={t.AriaLabelsOpenOnboardingPopup}>
                        <span className="cwt-icon-label">{t.ProfileCreateBtn}</span>
                        {open_outside}
                    </i>
                </a>);
            } else if (emptyState == enums.profilePageEmptyStates.NoUser) {
                profilePageEmptyStates = null;
            } else if (isSelf) {
                profilePageEmptyStates = null;
            } else {
                profilePageEmptyStates = (<div className="cwt-other-profile-settings">
                    <FollowButton {...{userId, managedClasses, usersConnector, t, mainTelemetryObject: this.telemetryObjects.profileCard}} />
                </div>);
            }
        } else {
            if (emptyState == enums.profilePageEmptyStates.NotSocialUser) {
                profilePageEmptyStates = (<a className="cwt-link" href="#">
                    <i className="cwt-icon cwt-link cwt-edit-profile-btn" onClick={this.openOnboardingPopup.bind(this)} role="button" aria-label={t.AriaLabelsOpenOnboardingPopup}>
                        <span className="cwt-icon-label">{t.ProfileCreateBtn}</span>
                        {open_outside}
                    </i>
                </a>);
            } else if (emptyState == enums.profilePageEmptyStates.NoUser) {
                profilePageEmptyStates = null;
            } else if (isSelf) {
                profilePageEmptyStates = (<a className="cwt-link" role="link" onClick={() => openEditProfilePage(theme, market, "_blank")}>
                    <i className="cwt-icon cwt-edit-profile-btn">
                        {pen}
                        <span className="cwt-icon-label">{t.ProfileEditBtn}</span>
                    </i>
                </a>);
            } else if (isReported) {
                profilePageEmptyStates = (<div role="alert"> <DisabledMessage {...{ managedClasses, message: t.ProfileCardUserReported, t, mainTelemetryObject: this.telemetryObjects.reportedMessage }} /> </div>);
            } else {
                profilePageEmptyStates = (<div className="cwt-other-profile-settings">
                    <FollowButton {...{userId, managedClasses, usersConnector, t, mainTelemetryObject: this.telemetryObjects.profileCard}} />
                    {reportMenuVisible &&
                        <ProfileReportMenu
                            t={t}
                            visible={reportMenuVisible}
                            onReport={this.reportHandler.bind(this)}
                            onHide={() => this.setState({ reportMenuVisible: false })}
                            managedClasses={managedClasses}
                            mainTelemetryObject={this.telemetryObjects.reportUserMenu}
                            theme={theme}
                            popupConnector={this.props.popupConnector}
                            responsiveClass={this.props.responsiveClass}
                            requestContext={this.props.requestContext}
                            renderInPopover={true}
                            id={this.props.id}
                            usersConnector={this.props.usersConnector}
                            me={this.props.me}
                        />
                    }
                    <NeutralButton className="cwt-fast-button"
                        type="button"
                        role="menu"
                        aria-haspopup="menu"
                        aria-label={t.AriaLabelsReportProfile}
                        onClick={this.showReportMenu.bind(this)} {...renderHybrid(this.telemetryObjects.moreButton, false)}>
                        <i className="cwt-icon">
                            {ellipsis}
                        </i>
                    </NeutralButton>
                </div>);
            }
        }
        return profilePageEmptyStates && <div className="cwt-action-panel"> {profilePageEmptyStates} </div>;
    }
}